import {
  type AdditionalPaymentMode,
  CampaignCreatorsActiveStatusFilter,
  type CampaignDetailsCreator,
  type CampaignStats,
} from '@metapic-apps/shared'
import { useFlag } from '@unleash/proxy-client-vue'
import { type Ref, ref, toValue, watch, watchEffect } from 'vue'

import {
  normalizeCampaign,
  normalizeCampaignCreatorsCount,
  normalizeCampaignCreatorsList,
  normalizeCampaignStats,
  serializeCampaign,
} from '@/lib/adapters/campaign.adapter'
import { serializeTrafficSourceCostsV2 } from '@/lib/adapters/traffic-source-costs.adapter'
import { FLAG_CAMPAIGN_PAYMENT_SPLIT } from '@/lib/constants/flags.constants'
import { useApi } from '@/lib/helpers/api.helpers'
import { handleErrorMessage } from '@/lib/helpers/error.helpers'
import { makeQueryString, makeUrlWithParams } from '@/lib/helpers/query.helpers'
import { request } from '@/lib/services'
import { type CampaignFormType, type CampaignType } from '@/lib/types/campaign.types'
import { type CampaignStatusEnum } from '@/lib/types/campaigns.types'
import type { TrafficSourceCostsType } from '@/lib/types/traffic-source-costs.types'
import { useErrorStore } from '@/stores/error'

const getCampaignPaymentMode = (
  campaignData: CampaignFormType,
  isPaymentSplitEnabled: boolean,
): AdditionalPaymentMode => {
  if (!isPaymentSplitEnabled) {
    return 'INFER_REVENUE_FROM_COST'
  }

  return campaignData.hasInvoiceAdvertiserDirectly && campaignData.revenueInvoice
    ? 'INDEPENDENTLY_SET'
    : 'COST_ONLY'
}

export const useGetCampaignRequest = (id: number) => {
  const isLoading = ref(false)
  const error = ref<Error | null>(null)
  const data = ref<CampaignType | null>(null)
  const errorStore = useErrorStore()

  const getCampaign = async () => {
    try {
      isLoading.value = true
      error.value = null
      const response = await request.get(
        makeUrlWithParams(`/v2/offers/${id}`, {
          'include[]': 'additional_payment.cost_creator',
        }),
      )
      const targets = await request.get(`/v2/offers/${id}/targets`)
      data.value = normalizeCampaign(response.data, targets.data)
    } catch (e: unknown) {
      errorStore.setError(handleErrorMessage(e))
      error.value = e as Error
    } finally {
      isLoading.value = false
    }
  }

  watchEffect(() => void getCampaign())

  return {
    data,
    getCampaign,
    isLoading,
    error,
  }
}

export const useGetCampaignDataRequest = (idRef: Ref<number | undefined>) =>
  useApi(async () => {
    const id = toValue(idRef)
    if (id === undefined) {
      return null
    }
    const response = await request.get(
      makeUrlWithParams(`/v2/offers/${id}`, {
        'include[]': 'additional_payment.cost_creator',
      }),
    )
    return normalizeCampaign(response.data)
  })

export const useDeleteCampaignRequest = () => {
  const isLoading = ref(false)
  const error = ref<Error | null>(null)

  const errorStore = useErrorStore()

  const deleteCampaign = async (id: number) => {
    try {
      isLoading.value = true
      error.value = null
      await request.delete(`/v2/offers/${id}`)
    } catch (e) {
      errorStore.setError(handleErrorMessage(e))
      error.value = e as Error
    } finally {
      isLoading.value = false
    }
  }

  return {
    deleteCampaign,
    isLoading,
    error,
  }
}

export const useCreateCampaignRequest = () => {
  const isLoading = ref(false)
  const error = ref<Error | null>(null)
  const errorStore = useErrorStore()
  const isPaymentSplitEnabled = useFlag(FLAG_CAMPAIGN_PAYMENT_SPLIT)

  const createCampaigns = async (campaignData: CampaignFormType) => {
    const data = serializeCampaign(
      campaignData,
      getCampaignPaymentMode(campaignData, isPaymentSplitEnabled.value),
    )

    try {
      isLoading.value = true
      error.value = null
      const responses = await Promise.all(
        data.campaigns.map((store) => {
          return request.post(`/v2/stores/${store.store_id}/offers`, store)
        }),
      )

      if (data.image) {
        await Promise.all(
          responses.map((campaign) => {
            return request.post(`/v2/offers/${campaign.data.data.id}/uploads`, data.image, {
              isFormData: true,
            })
          }),
        )
      }
    } catch (e: unknown) {
      errorStore.setError(handleErrorMessage(e))
      error.value = e as Error
    } finally {
      isLoading.value = false
    }
  }

  return {
    createCampaign: createCampaigns,
    isLoading,
    error,
  }
}

export const useEditCampaignRequest = (campaignId: number) => {
  const isLoading = ref(false)
  const error = ref<Error | null>(null)
  const errorStore = useErrorStore()
  const isPaymentSplitEnabled = useFlag(FLAG_CAMPAIGN_PAYMENT_SPLIT)

  const editCampaign = async (campaignData: CampaignFormType) => {
    const data = serializeCampaign(
      campaignData,
      getCampaignPaymentMode(campaignData, isPaymentSplitEnabled.value),
    )
    try {
      error.value = null
      isLoading.value = true

      await request.put(`/v2/offers/${campaignId}`, data.campaigns[0])
      if (data.image) {
        await request.post(`/v2/offers/${campaignId}/uploads`, data.image, { isFormData: true })
      }
    } catch (e: unknown) {
      errorStore.setError(handleErrorMessage(e))
      error.value = e as Error
    } finally {
      isLoading.value = false
    }
  }

  return {
    editCampaign,
    isLoading,
    error,
  }
}

export const useSetCampaignActivityRequest = (campaignId: number) => {
  const isLoading = ref(false)
  const error = ref<Error | null>(null)
  const errorStore = useErrorStore()

  const setStatus = async (shouldPause: boolean): Promise<CampaignStatusEnum | undefined> => {
    try {
      error.value = null
      isLoading.value = true
      const url = shouldPause
        ? `/v2/offers/${campaignId}/pause`
        : `/v2/offers/${campaignId}/unpause`
      const { data } = await request.post(url, {})
      return data.status
    } catch (e: unknown) {
      errorStore.setError(handleErrorMessage(e))
      error.value = e as Error
    } finally {
      isLoading.value = false
    }
  }

  return {
    setStatus,
    isLoading,
    error,
  }
}

export const useCampaignStatsRequest = (
  campaignId: number,
  filterRef: Ref<{ startDate: string; endDate: string }>,
) => {
  const isLoading = ref(false)
  const data = ref<CampaignStats>()
  const error = ref<Error | null>(null)
  const errorStore = useErrorStore()

  const getStats = async (query: string) => {
    try {
      error.value = null
      isLoading.value = true
      const response = await request.get(`/v2/offers/${campaignId}/stats?${query}`)
      data.value = normalizeCampaignStats(response.data)
    } catch (e: unknown) {
      errorStore.setError(handleErrorMessage(e))
      error.value = e as Error
    } finally {
      isLoading.value = false
    }
  }

  watch(
    filterRef,
    async () => {
      const filter = toValue(filterRef)
      const query = makeQueryString(filter)
      if (query) {
        await getStats(query)
      }
    },
    { immediate: true },
  )

  return {
    data,
    isLoading,
    error,
  }
}

export const useGetCampaignCreatorsListRequest = ({
  id,
  page,
  status,
  searchQuery,
  sortBy,
  dateRange,
  currency,
}: {
  id: number
  status: Ref<CampaignCreatorsActiveStatusFilter>
  page?: Ref<number>
  searchQuery?: Ref<string>
  currency: Ref<string | undefined>
  sortBy: {
    field: Ref<string>
    order: Ref<'asc' | 'desc'>
  }
  dateRange: Ref<{ startDate: string; endDate: string }>
}) => {
  const errorStore = useErrorStore()
  const error = ref<Error | null>(null)
  const isLoading = ref(false)

  const data = ref<CampaignDetailsCreator[] | undefined>(undefined)
  const totalPages = ref(0)

  const fetchCreatorsList = async () => {
    const currencyVal = toValue(currency)
    if (!currencyVal) {
      return
    }
    try {
      isLoading.value = true
      error.value = null
      const statusVal = toValue(status)
      const pageVal = toValue(page)
      const searchQueryVal = toValue(searchQuery)
      const sortByVal = toValue(sortBy.order)
      const fieldVal = toValue(sortBy.field)
      const { startDate, endDate } = toValue(dateRange)

      const response = await request.get(
        makeUrlWithParams(`/v2/offers/${id}/participants-stats`, {
          active: String(statusVal === CampaignCreatorsActiveStatusFilter.Active),
          start_date: startDate,
          end_date: endDate,
          ...(statusVal === CampaignCreatorsActiveStatusFilter.Active && {
            sort_by: `${fieldVal}:${sortByVal}`,
          }),
          ...(pageVal && { page: String(pageVal) }),
          ...(searchQueryVal && { query: searchQueryVal }),
        }),
      )
      data.value = normalizeCampaignCreatorsList(response.data.data, currencyVal)
      totalPages.value = response.data.meta.last_page
    } catch (e: unknown) {
      errorStore.setError(handleErrorMessage(e))
      error.value = e as Error
    } finally {
      isLoading.value = false
    }
  }

  watch(
    [status, page, sortBy.order, sortBy.field, dateRange, searchQuery, currency],
    async () => {
      await fetchCreatorsList()
    },
    { immediate: true },
  )

  return {
    data,
    isLoading,
    error,
    totalPages,
    fetchCreatorsList,
  }
}

export const useGetCampaignCreatorsCount = ({
  id,
  dateRange,
}: {
  id: number
  dateRange: Ref<{ startDate: string; endDate: string }>
}) => {
  const errorStore = useErrorStore()
  const error = ref<Error | null>(null)
  const isLoading = ref(false)

  const data = ref<{ activeUsers: number; inactiveUsers: number } | undefined>(undefined)

  const fetchStats = async () => {
    try {
      isLoading.value = true
      error.value = null

      const response = await request.get(
        makeUrlWithParams(`/v2/offers/${id}/participants-count-by-click-activity`, {
          start_date: dateRange.value.startDate,
          end_date: dateRange.value.endDate,
        }),
      )
      data.value = normalizeCampaignCreatorsCount(response.data)
    } catch (e: unknown) {
      errorStore.setError(handleErrorMessage(e))
      error.value = e as Error
    } finally {
      isLoading.value = false
    }
  }

  watch(
    dateRange,
    async () => {
      await fetchStats()
    },
    { immediate: true },
  )

  return {
    data,
    isLoading,
    error,
  }
}

export const useUpdateParticipantClickLimitRequest = () => {
  const errorStore = useErrorStore()
  const error = ref<Error | null>(null)
  const isLoading = ref(false)

  const overrideClickLimit = async (
    campaignId: number,
    participantId: string,
    limit: number | null,
  ) => {
    try {
      isLoading.value = true
      error.value = null
      await request.patch(
        `/v2/offers/${campaignId}/participants/${participantId}/overrides/clicks`,
        { click_limit: limit },
      )
    } catch (e: unknown) {
      errorStore.setError(handleErrorMessage(e))
      error.value = e as Error
    } finally {
      isLoading.value = false
    }
  }

  return {
    overrideClickLimit,
    isLoading,
    error,
  }
}

export const useUpdateParticipantCostsRequest = () => {
  const errorStore = useErrorStore()
  const error = ref<Error | null>(null)
  const isLoading = ref(false)

  const overrideCosts = async (
    campaignId: number,
    participantId: string,
    trafficSourceCots: TrafficSourceCostsType[] | null,
    defaultCurrency: string,
  ) => {
    try {
      isLoading.value = true
      error.value = null
      const omitFieldsInPayload = ['invoice_cpc', 'invoice_cpa']
      await request.patch(
        `/v2/offers/${campaignId}/participants/${participantId}/overrides/costs`,
        {
          traffic_sources_costs: trafficSourceCots
            ? serializeTrafficSourceCostsV2(trafficSourceCots, defaultCurrency, omitFieldsInPayload)
            : null,
        },
      )
    } catch (e: unknown) {
      errorStore.setError(handleErrorMessage(e))
      error.value = e as Error
    } finally {
      isLoading.value = false
    }
  }

  return {
    overrideCosts,
    isLoading,
    error,
  }
}
